<script setup>
import PostCard from '@components/PostCard.vue'
import Pagination from '@components/Pagination.vue'
import {ref, computed, watch, onMounted} from 'vue'
import Icon from '@components/Icon.vue'
import { $isSearchOpen } from '../store'

const props = defineProps({
  posts: {
    type: Array,
    required: true,
  },
})

let tags = {};
props.posts.flatMap(({ tags }) => tags).forEach(tag => {
  if (tags[tag.id]) {
    return false
  }
  tags[tag.id] = tag
})
tags = Object.values(tags)

const isFilterOpen = ref(false)
const page = ref(1)
const activeTags = ref([])

onMounted(() => {
  const params = new URLSearchParams(window.location.search)
  if (params.has('tags')) {
    let tagNames = params.get('tags')?.split(',') || []
    activeTags.value = tags.filter(tag => tagNames.includes(tag.name))
  }
  page.value = params.has('page') ? parseInt(params.get('page')) : 1
})

watch(activeTags, value => {
  setUrlParam('tags', value.map(tag => encodeURI(tag.name)).join(','))
  page.value = 1
})
watch(page, value => {
  setUrlParam('page', value)
})

const filteredPosts = computed(() => {
  let fPosts = props.posts

  if (activeTags.value?.length > 0) {
    fPosts = fPosts.filter(post => {
      return post.tags.map(({ id }) => id).filter(tagId => activeTags.value.map(({ id }) => id).includes(tagId)).length > 0
    })
  }

  return fPosts
})
const pages = computed(() => {
  return Math.ceil(filteredPosts.value.length / 9)
})
const filteredPostPaginated = computed(() => {
  if (!page.value) {
    return filteredPosts.value
  }
  const startIndex = (page.value - 1) * 9
  return filteredPosts.value.slice(startIndex, startIndex + 9)
})

function goToPage(p) {
  page.value = p
  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  });
}

function setUrlParam(name, value) {
  const params = new URLSearchParams(window.location.search);
  if (value) {
    params.set(name, value)
  } else {
    params.delete(name)
  }
  window.history.replaceState({}, "", decodeURIComponent(`${window.location.pathname}?${params}`));
}
</script>
<template>
  <div class="blog__filters" :class="{ 'blog__filters--open': isFilterOpen }">
    <div class="blog__filters-overlay" @click="isFilterOpen = false"></div>

    <div class="blog__filters-inner">
      <a href="#" @click.prevent="isFilterOpen = false" class="blog__filters-close">
        <Icon type="close-circle" />
      </a>

      <div class="blog__filters-title">FILTER</div>

      <div class="blog__filters-container">
        <div v-if="tags.length > 1">
          <div class="blog__filters-name">By Category</div>
          <div class="blog__filters-options">
            <div class="blog__filters-option" v-for="tag in tags" :key="tag.id">
              <input
                  type="checkbox"
                  :value="tag"
                  v-model="activeTags"
                  :id="`tag-${tag.id}`"
              />
              <label :for="`tag-${tag.id}`">{{ tag.name }}</label>
            </div>
          </div>
        </div>
      </div>

      <button class="blog__filters-apply" type="button" @click="isFilterOpen = false">APPLY FILTER</button>
    </div>
  </div>

  <div class="blog__bar">
    <div class="blog__buttons">
      <button type="button" class="blog__filter" @click="isFilterOpen = true">
        <span>Filters</span>
        <Icon type="filter-2" />
      </button>

      <button type="button" class="blog__search" @click="$isSearchOpen.set(true)">
        <span>Search</span>
        <Icon type="search-2" />
      </button>
    </div>

    <div class="blog__tags">
      <div class="blog__tag" v-for="tag in activeTags" :key="tag.id">
        <input
            type="checkbox"
            :value="tag"
            v-model="activeTags"
            :id="`tag-${tag.id}`"
        />
        <label :for="`tag-${tag.id}`">
          <span>{{ tag.name }}</span>
          <svg xmlns="http://www.w3.org/2000/svg" width="11" height="12" viewBox="0 0 11 12" fill="none">
            <rect x="10.0664" y="0.510742" width="1.29404" height="14.2344" rx="0.64702" transform="rotate(45 10.0664 0.510742)" fill="white"/>
            <rect x="10.9805" y="10.5742" width="1.29404" height="14.2344" rx="0.647021" transform="rotate(135 10.9805 10.5742)" fill="white"/>
          </svg>
        </label>
      </div>
    </div>
  </div>
  <div>
    <div class="blog__posts">
      <PostCard v-for="post in filteredPostPaginated" :key="post.id" :post="post" :excerpt="false" :tags="true" />
    </div>
    <Pagination class="blog__pagination" :pages="pages" :current-page="page" @paginate="goToPage" />
  </div>
</template>

<style lang="scss" scoped>
.blog__filters {
  background: rgba(0, 0, 0, 0.35);
  display: flex;
  height: 100%;
  justify-content: flex-start;
  left: 0;
  position: fixed;
  top: 0;
  transition: visibility var(--duration-default) ease;
  width: 100vw;
  z-index: 2147483001;
  visibility: hidden;

  &.blog__filters--open {
    visibility: visible;
  }

  .blog__filters-overlay {
    bottom: 0;
    left: 0;
    position: fixed;
    right: 0;
    top: 0;
  }

  &.blog__filters--open .blog__filters-inner {
    transform: translateX(0);
  }

  .blog__filters-close {
    position: absolute;
    top: 12px;
    right: 16px;
    background-color: #EBEBEA;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;

    @media screen and (min-width: 768px) {
      top: 12px;
      right: 20px;
      width: 32px;
      height: 32px;
    }
  }

  .blog__filters-apply {
    color: var(--Neutral-50, #F5F5F5);
    text-align: center;
    font-family: Outfit, 'Outfit Fallback', Arial, serif;
    font-size: 12px;
    font-weight: 700;
    line-height: 15px; /* 125% */
    border-radius: 21px;
    background: var(--Orange---terracotta, #DB7E5A);
    padding: 10px;
    border: 0;
    cursor: pointer;
  }

  .blog__filters-inner {
    max-width: calc(100vw - 3rem);
    transform: translateX(100%);
    transition: transform var(--duration-default) ease;
    width: 32rem;
    background: var(--shades-white, #FFF);
    box-shadow: 0 4px 6px -4px rgba(16, 24, 40, 0.10), 0px 10px 15px -3px rgba(16, 24, 40, 0.10);
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow-y: auto;
    padding: 12px 16px 16px 16px;

    @media screen and (min-width: 768px) {
      padding: 12px 60px 16px 60px;
    }

    .blog__filters-title {
      color: var(--Neutral-500, #9D9B99);
      font-family: Outfit, 'Outfit Fallback', Arial, serif;
      font-size: 16px;
      font-weight: 700;
      line-height: 24px; /* 150% */
      margin-bottom: 14px;

      @media screen and (min-width: 768px) {
        font-size: 20px;
        margin-bottom: 28px;
      }
    }

    .blog__filters-container {
      flex: 1;
    }

    .blog__filters-name {
      color: var(--Neutral-900, #4F4C47);
      font-family: Outfit, 'Outfit Fallback', Arial, serif;
      font-size: 12px;
      font-weight: 800;
      line-height: 20px; /* 166.667% */
      margin-bottom: 8px;

      @media screen and (min-width: 768px) {
        font-size: 16px;
        margin-bottom: 20px;
      }
    }

    .blog__filters-options {
      margin-bottom: 12px;
      display: flex;
      flex-wrap: wrap;
      gap: 8px;

      @media screen and (min-width: 768px) {
        margin-bottom: 36px;
      }

      input {
        border: 0;
        clip: rect(0 0 0 0);
        height: 1px;
        margin: -1px;
        overflow: hidden;
        padding: 0;
        position: absolute;
        width: 1px;
      }

      label {
        color: var(--Neutral-700, #767370);
        text-align: center;
        font-family: Outfit, 'Outfit Fallback', Arial, serif;
        font-size: 14px;
        font-weight: 500;
        line-height: 20px; /* 142.857% */
        border-radius: 32px;
        background: var(--Neutral-50, #F5F5F5);
        padding: 4px 10px;
        cursor: pointer;

        @media screen and (min-width: 768px) {
          font-size: 16px;
          padding: 4px 12px;
        }
      }

      input:checked + label {
        background: var(--Primary-600, #183D4F);
        color: var(--Neutral-50, #F5F5F5);
      }
    }
  }
}

.blog__bar {
  background: var(--neutral-50, #F5F5F5);

  .blog__buttons {
    display: flex;
    gap: 12px;
    margin-bottom: 12px;

    button {
      display: flex;
      padding: 8px 16px;
      align-items: center;
      border-radius: 4px;
      gap: 16px;
      background: var(--Neutral-100, #F1EFE8);
      color: var(--Neutral-600, #8A8885);
      font-family: Outfit, 'Outfit Fallback', Arial, serif;
      font-size: 14px;
      font-weight: 500;
      line-height: 20px; /* 142.857% */
      border: 0;
      cursor: pointer;

      :deep(svg) {
        width: 18px;
        height: auto;

        path {
          fill: #8A8885;
        }
      }
    }
  }

  .blog__tags {
    margin-bottom: 12px;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;

    @media screen and (min-width: 768px) {
      margin-bottom: 36px;
    }

    input {
      border: 0;
      clip: rect(0 0 0 0);
      height: 1px;
      margin: -1px;
      overflow: hidden;
      padding: 0;
      position: absolute;
      width: 1px;
    }

    label {
      display: flex;
      align-items: center;
      gap: 10px;
      color: var(--Neutral-50, #F5F5F5);
      text-align: center;
      font-family: Outfit, 'Outfit Fallback', Arial, serif;
      font-size: 16px;
      font-weight: 600;
      line-height: 20px; /* 142.857% */
      border-radius: 32px;
      background: var(--Light-navy, #183D4F);
      padding: 4px 10px;
      cursor: pointer;

      @media screen and (min-width: 768px) {
        font-size: 16px;
        padding: 4px 12px;
      }
    }
  }
}

.blog__posts {
  display: grid;
  grid-template-columns: auto;
  gap: 32px;

  @media screen and (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 32px;
  }
}

.blog__pagination {
  border-top: 1px solid #C4C3C1;
  padding-top: 28px;
  margin-top: 32px;

  @media screen and (min-width: 768px) {
    margin-top: 64px;
  }
}
</style>
